import { Toaster } from "sonner";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";import { useTranslation } from "react-i18next";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth.jsx";
import { ThemeProvider } from "@/lib/theme.jsx";
import { HelpModeProvider } from "@/lib/help.jsx";
import { ConfirmProvider } from "@/lib/confirm.jsx";
import AppShell from "@/components/AppShell";
import PlatformShell from "@/components/PlatformShell";
import LoginPage from "@/pages/Login";
import SignupPage from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
// Phase 6o Hotfix H — Providers / Resellers / Allocation pages retired;
// the Elswitch data model is now a single flat "org has customers"
// hierarchy (no tier 1 / tier 2 parents). Their imports, routes, and
// sidebar entries are gone.
import CustomersPage from "@/pages/Customers";
import TrunksPage from "@/pages/Trunks";
import DomainsPage from "@/pages/Domains";
import RoutingPage from "@/pages/Routing";
import ACLsPage from "@/pages/ACLs";
import LivePage from "@/pages/Live";
import CDRsPage from "@/pages/CDRs";
import SecurityPage from "@/pages/Security";
import SettingsPage from "@/pages/Settings";
import UsersPage from "@/pages/Users";
import BillingPage from "@/pages/Billing";
import StripeReturnPage from "@/pages/StripeReturn";
import { SecurityPostureBanner } from "@/components/SecurityPostureBanner";
import { LegalConsentModal } from "@/components/LegalConsentModal";
import AuditPage from "@/pages/Audit";
import FirewallPage from "@/pages/Firewall";
import DocsPage from "@/pages/Docs";
import IngressPage from "@/pages/Ingress";
import PricingPage from "@/pages/PricingPage";
import VerifyEmailPage from "@/pages/VerifyEmail";
import LegalPage from "@/pages/LegalPage";
import PlatformOverview from "@/pages/platform/Overview";
import TenantsAdmin from "@/pages/platform/Tenants";
import PlatformSettings from "@/pages/platform/PlatformSettings";
import PlatformSecurity from "@/pages/platform/PlatformSecurity";
import PlatformBilling from "@/pages/platform/PlatformBilling";
import PlatformPlans from "@/pages/platform/PlatformPlans";
import PlatformSystem from "@/pages/platform/PlatformSystem";

function TenantLayout() {
  const { user, loading, scope, isPending, org } = useAuth();
  const { t } = useTranslation();
  if (loading) return <div className="min-h-screen grid place-items-center bg-background"><div className="text-sm text-muted-foreground">Loading…</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (scope === "platform") return <Navigate to="/platform" replace />;
  const sipPaused = org?.sip_enabled === false;
  return (
    <AppShell>
      {isPending && (
        <div className="mb-4 rounded-md border border-[hsl(var(--warn))]/40 bg-[hsl(var(--warn))]/10 text-sm px-4 py-3" data-testid="pending-banner">
          {t("banner.pending", { name: org?.name || "", status: org?.status || "" })}
        </div>
      )}
      {!isPending && sipPaused && (
        <div className="mb-4 rounded-md border border-[hsl(var(--warn))]/40 bg-[hsl(var(--warn))]/10 text-sm px-4 py-3" data-testid="sip-paused-banner">
          {t("banner.sipPaused")}
        </div>
      )}
      <Outlet />
      <LegalConsentModal />
    </AppShell>
  );
}

function PlatformLayout() {
  const { user, loading, scope } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center bg-background"><div className="text-sm text-muted-foreground">Loading…</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (scope !== "platform") return <Navigate to="/" replace />;
  return (
    <PlatformShell>
      <SecurityPostureBanner />
      <Outlet />
      <LegalConsentModal />
    </PlatformShell>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <HelpModeProvider>
          <ConfirmProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/platform" element={<PlatformLayout />}>
              <Route index element={<PlatformOverview />} />
              <Route path="tenants" element={<TenantsAdmin />} />
              <Route path="plans" element={<PlatformPlans />} />
              <Route path="billing" element={<PlatformBilling />} />
              <Route path="firewall" element={<FirewallPage scope="platform" />} />
              <Route path="audit" element={<AuditPage platform />} />
              <Route path="settings" element={<PlatformSettings />} />
              <Route path="security" element={<PlatformSecurity />} />
              <Route path="system" element={<PlatformSystem />} />
            </Route>
            <Route element={<TenantLayout />}>
              <Route path="/" element={<Dashboard />} />
              {/* Phase 6o Hotfix H — /providers, /resellers, /allocation
                  routes retired. Any bookmarked link redirects to the
                  flat customers list, which is the only tenant-level
                  party list now. */}
              <Route path="/providers" element={<Navigate to="/customers" replace />} />
              <Route path="/resellers" element={<Navigate to="/customers" replace />} />
              <Route path="/allocation" element={<Navigate to="/customers" replace />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/trunks" element={<TrunksPage />} />
              <Route path="/domains" element={<DomainsPage />} />
              <Route path="/routing" element={<RoutingPage />} />
              <Route path="/acls" element={<ACLsPage />} />
              <Route path="/live" element={<LivePage />} />
              <Route path="/cdrs" element={<CDRsPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/billing/checkout/return" element={<StripeReturnPage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/ingress" element={<IngressPage />} />
              <Route path="/firewall" element={<FirewallPage scope="tenant" />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/staff" element={<UsersPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/docs" element={<DocsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster
            position="top-right"
            richColors
            closeButton
            // Phase 6n — push toasts below the 52-px utility bar so
            // they stop overlapping the language / theme / admin
            // controls (IMG_0289). 64 px clears the bar + a 12 px gap.
            offset="64px"
          />
        </BrowserRouter>
        </ConfirmProvider>
        </HelpModeProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
